// sha256.ts — secure-context 友好的 SHA-256 hex 摘要。
//
// 背景(L4-B16a / 同 [[uuid]] 的 L4-A16 根因):`crypto.subtle`(Web Crypto SubtleCrypto)
// 只在 Secure Context(HTTPS / localhost / 127.0.0.1 / file://)下暴露。L4 手测从局域网
// IP `http://192.168.x.x:8081` 访问 SPA 时非 secure context → `crypto.subtle === undefined`
// → 上传大文件 `crypto.subtle.digest('SHA-256', buf)` 读属性崩(upload.ts 卡 0%)。
//
// 这里给**内容完整性校验**用(秒传索引 sha256_original + 分片 Upload-Checksum),
// 不作密码学随机/签名。crypto.subtle 可用时走原生(快、硬件加速);否则回落纯 JS 实现。
// 纯 JS 实现按 FIPS 180-4 标准,有 known-vector 测试(sha256.test.ts)防回潮。

import { createSHA256 } from 'hash-wasm'

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])

function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n))
}

// sha256Pure 是 FIPS 180-4 标准 SHA-256(纯 JS),输入 Uint8Array → 32 字节摘要。
function sha256Pure(msg: Uint8Array): Uint8Array {
  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ])

  // padding:append 0x80,补 0 到 length % 64 === 56,再补 64-bit big-endian bit length
  const bitLen = msg.length * 8
  const withOne = msg.length + 1
  const padLen = ((withOne + 8 + 63) & ~63) - msg.length
  const buf = new Uint8Array(msg.length + padLen)
  buf.set(msg)
  buf[msg.length] = 0x80
  // bit length(64-bit big-endian);JS number 安全整数 < 2^53,高 32 位用浮点除法
  const hi = Math.floor(bitLen / 0x100000000)
  const lo = bitLen >>> 0
  const dv = new DataView(buf.buffer)
  dv.setUint32(buf.length - 8, hi)
  dv.setUint32(buf.length - 4, lo)

  // 固定长度 typed array,下标恒在界内;`!` 仅为满足 noUncheckedIndexedAccess。
  const w = new Uint32Array(64)
  for (let off = 0; off < buf.length; off += 64) {
    for (let i = 0; i < 16; i++) {
      w[i] = dv.getUint32(off + i * 4)
    }
    for (let i = 16; i < 64; i++) {
      const w15 = w[i - 15]!
      const w2 = w[i - 2]!
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3)
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10)
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0
    }

    let a = h[0]!
    let b = h[1]!
    let c = h[2]!
    let d = h[3]!
    let e = h[4]!
    let f = h[5]!
    let g = h[6]!
    let hh = h[7]!
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)
      const ch = (e & f) ^ (~e & g)
      const t1 = (hh + S1 + ch + K[i]! + w[i]!) >>> 0
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) >>> 0
      hh = g
      g = f
      f = e
      e = (d + t1) >>> 0
      d = c
      c = b
      b = a
      a = (t1 + t2) >>> 0
    }
    h[0] = (h[0]! + a) >>> 0
    h[1] = (h[1]! + b) >>> 0
    h[2] = (h[2]! + c) >>> 0
    h[3] = (h[3]! + d) >>> 0
    h[4] = (h[4]! + e) >>> 0
    h[5] = (h[5]! + f) >>> 0
    h[6] = (h[6]! + g) >>> 0
    h[7] = (h[7]! + hh) >>> 0
  }

  const out = new Uint8Array(32)
  const odv = new DataView(out.buffer)
  for (let i = 0; i < 8; i++) odv.setUint32(i * 4, h[i]!)
  return out
}

function toHex(bytes: Uint8Array): string {
  let s = ''
  for (let i = 0; i < bytes.length; i++) {
    s += bytes[i]!.toString(16).padStart(2, '0')
  }
  return s
}

// 增量 SHA-256:维护 h[8] 状态 + <64 字节余量 block + 总字节数。
// 供大文件分片流式哈希(纯 JS fallback;hash-wasm 不可用时用)。
export function createSha256Incremental() {
  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ])
  const block = new Uint8Array(64)
  const blockDv = new DataView(block.buffer)
  const w = new Uint32Array(64)
  let bufLen = 0
  let total = 0 // 累计字节数

  function compress(dv: DataView, off: number): void {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4)
    for (let i = 16; i < 64; i++) {
      const w15 = w[i - 15]!
      const w2 = w[i - 2]!
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3)
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10)
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0
    }
    let a = h[0]!, b = h[1]!, c = h[2]!, d = h[3]!
    let e = h[4]!, f = h[5]!, g = h[6]!, hh = h[7]!
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)
      const ch = (e & f) ^ (~e & g)
      const t1 = (hh + S1 + ch + K[i]! + w[i]!) >>> 0
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) >>> 0
      hh = g; g = f; f = e; e = (d + t1) >>> 0
      d = c; c = b; b = a; a = (t1 + t2) >>> 0
    }
    h[0] = (h[0]! + a) >>> 0
    h[1] = (h[1]! + b) >>> 0
    h[2] = (h[2]! + c) >>> 0
    h[3] = (h[3]! + d) >>> 0
    h[4] = (h[4]! + e) >>> 0
    h[5] = (h[5]! + f) >>> 0
    h[6] = (h[6]! + g) >>> 0
    h[7] = (h[7]! + hh) >>> 0
  }

  return {
    update(bytes: Uint8Array): void {
      total += bytes.length
      let i = 0
      // 先填满上次的余量 block
      if (bufLen > 0) {
        while (bufLen < 64 && i < bytes.length) block[bufLen++] = bytes[i++]!
        if (bufLen === 64) {
          compress(blockDv, 0)
          bufLen = 0
        }
      }
      // 直接消费 bytes 中的整 64 字节块(用 bytes 自己的 DataView,注意 byteOffset)
      const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
      for (; i + 64 <= bytes.length; i += 64) compress(dv, i)
      // 余量留到下次
      while (i < bytes.length) block[bufLen++] = bytes[i++]!
    },
    final(): Uint8Array {
      const bitLen = total * 8
      block[bufLen++] = 0x80
      if (bufLen > 56) {
        while (bufLen < 64) block[bufLen++] = 0
        compress(blockDv, 0)
        bufLen = 0
      }
      while (bufLen < 56) block[bufLen++] = 0
      const hi = Math.floor(bitLen / 0x100000000)
      blockDv.setUint32(56, hi)
      blockDv.setUint32(60, bitLen >>> 0)
      compress(blockDv, 0)
      const out = new Uint8Array(32)
      const odv = new DataView(out.buffer)
      for (let i = 0; i < 8; i++) odv.setUint32(i * 4, h[i]!)
      return out
    },
  }
}

// sha256HexBuffer 计算 ArrayBuffer 的 SHA-256 hex(lowercase)。
// crypto.subtle 可用 → 原生;否则纯 JS fallback(非 secure context)。
export async function sha256HexBuffer(buf: ArrayBuffer): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (subtle && typeof subtle.digest === 'function') {
    const digest = await subtle.digest('SHA-256', buf)
    return toHex(new Uint8Array(digest))
  }
  return toHex(sha256Pure(new Uint8Array(buf)))
}

// sha256HexBlob 计算 Blob/File 的 SHA-256 hex(读 arrayBuffer 后转交 sha256HexBuffer)。
export async function sha256HexBlob(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer()
  return sha256HexBuffer(buf)
}

const HASH_CHUNK = 8 * 1024 * 1024 // 8MB 分片:小读绕开 ~2GB 单 ArrayBuffer 上限

// sha256HexBlobStreaming 分片流式计算 Blob/File 的 SHA-256 hex。
// 优先 hash-wasm(接近原生、非 secure-context 可用);异常回落纯 JS 增量器。
// onProgress(fraction 0..1) 每分片回报一次,末尾必为 1。
//
// 根因:旧 sha256HexBlob 用 blob.arrayBuffer() 把整文件读进单个 ArrayBuffer,
// >2GB 触发浏览器单 ArrayBuffer 上限 → NotReadableError;分片小读根治之。
export async function sha256HexBlobStreaming(
  blob: Blob,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const size = blob.size
  try {
    const hasher = await createSHA256()
    hasher.init()
    let off = 0
    while (off < size) {
      const end = Math.min(off + HASH_CHUNK, size)
      const buf = await blob.slice(off, end).arrayBuffer()
      hasher.update(new Uint8Array(buf))
      off = end
      onProgress?.(off / size)
    }
    if (size === 0) onProgress?.(1)
    return hasher.digest('hex')
  } catch {
    // fallback:纯 JS 增量器(hash-wasm 加载/运行失败的极端环境)
    const inc = createSha256Incremental()
    let off = 0
    while (off < size) {
      const end = Math.min(off + HASH_CHUNK, size)
      const buf = await blob.slice(off, end).arrayBuffer()
      inc.update(new Uint8Array(buf))
      off = end
      onProgress?.(off / size)
    }
    onProgress?.(1)
    return toHex(inc.final())
  }
}
